import type { SupabaseClient } from "@supabase/supabase-js";
import type { ObservabilityClient } from "../../observability/types";
import type { Database } from "../../types/database";
import { AgentRuntime } from "../agents/agentRuntime";
import { CommandBus } from "../commands/command";
import { ApprovalEngine } from "../cyan/approvalEngine";
import { ContextAssemblyService } from "../cyan/contextAssemblyService";
import { CyanLiveService } from "../cyan/cyanLiveService";
import { KnowledgeGraphService } from "../cyan/knowledgeGraphService";
import {
  StaticModelRouter,
  DeterministicModelProvider,
} from "../cyan/modelProvider";
import { RequestInterpreter } from "../cyan/requestInterpreter";
import { SupabaseCyanIntelligenceRepository } from "../cyan/supabaseCyanRepository";
import { ToolIntentService } from "../cyan/toolIntentService";
import { ConversationManager } from "../cyan/conversationManager";
import { CyanCore } from "../cyan/cyanCore";
import { DecisionEngine } from "../cyan/decisionEngine";
import { ExecutionCoordinator } from "../cyan/executionCoordinator";
import { GoalManager } from "../cyan/goalManager";
import { PlanningEngine } from "../cyan/planningEngine";
import {
  InMemoryCyanIntelligenceRepository,
  type CyanIntelligenceRepository,
} from "../cyan/repositories";
import { ReasoningPipeline } from "../cyan/reasoningPipeline";
import { ReflectionEngine } from "../cyan/reflectionEngine";
import { TaskPlanner } from "../cyan/taskPlanner";
import { EventBus } from "../events/eventBus";
import { KnowledgeEngine } from "../knowledge/knowledgeEngine";
import { MemoryEngine } from "../memory/memory";
import type { PersistentMemoryRepository } from "../memory/persistentMemoryRepository";
import { SupabasePersistentMemoryRepository } from "../memory/supabasePersistentMemoryRepository";
import { GroundedMemoryRetrievalService } from "../memory/groundedMemoryRetrievalService";
import { EnterprisePermissionEvaluator } from "../permissions/permissionModel";
import {
  createDefaultServiceRegistry,
  ServiceRegistry,
} from "../services/serviceRegistry";
import { TaskEngine } from "../tasks/taskEngine";
import { createDefaultToolRegistry, ToolRegistry } from "../tools/toolRegistry";

import type { ExecutionJobRepository } from "../execution/jobRepository";
import { ExecutionJobService } from "../execution/jobService";
import { SupabaseExecutionJobRepository } from "../execution/supabaseJobRepository";

import { CyanLiveTakeoverBridge } from "../cyan/orchestration/cyanLiveTakeoverBridge";
import {
  createLiveTakeoverRuntime,
  type LiveTakeoverRuntimeDependencies,
} from "../cyan/orchestration/createLiveTakeoverRuntime";

import { createServiceToken, RuntimeContainer } from "./container";

export const TOKENS = {
  supabase: Symbol("supabase"),
  eventBus: createServiceToken<EventBus>("EventBus"),
  observability: createServiceToken<ObservabilityClient>("ObservabilityClient"),
  commandBus: createServiceToken<CommandBus>("CommandBus"),
  memoryEngine: createServiceToken<MemoryEngine>("MemoryEngine"),
  persistentMemoryRepository: createServiceToken<
    PersistentMemoryRepository | undefined
  >("PersistentMemoryRepository"),
  groundedMemoryRetrievalService: createServiceToken<
    GroundedMemoryRetrievalService | undefined
  >("GroundedMemoryRetrievalService"),
  knowledgeEngine: createServiceToken<KnowledgeEngine>("KnowledgeEngine"),
  permissionEvaluator: createServiceToken<EnterprisePermissionEvaluator>(
    "EnterprisePermissionEvaluator",
  ),
  toolRegistry: createServiceToken<ToolRegistry>("ToolRegistry"),
  taskEngine: createServiceToken<TaskEngine>("TaskEngine"),
  agentRuntime: createServiceToken<AgentRuntime>("AgentRuntime"),
  cyanRepository: createServiceToken<CyanIntelligenceRepository>(
    "CyanIntelligenceRepository",
  ),
  goalManager: createServiceToken<GoalManager>("GoalManager"),
  taskPlanner: createServiceToken<TaskPlanner>("TaskPlanner"),
  planningEngine: createServiceToken<PlanningEngine>("PlanningEngine"),
  decisionEngine: createServiceToken<DecisionEngine>("DecisionEngine"),
  reflectionEngine: createServiceToken<ReflectionEngine>("ReflectionEngine"),
  conversationManager: createServiceToken<ConversationManager>(
    "ConversationManager",
  ),
  reasoningPipeline: createServiceToken<ReasoningPipeline>("ReasoningPipeline"),
  executionCoordinator: createServiceToken<ExecutionCoordinator>(
    "ExecutionCoordinator",
  ),
  cyanCore: createServiceToken<CyanCore>("CyanCore"),
  requestInterpreter:
    createServiceToken<RequestInterpreter>("RequestInterpreter"),
  contextAssemblyService: createServiceToken<ContextAssemblyService>(
    "ContextAssemblyService",
  ),
  approvalEngine: createServiceToken<ApprovalEngine>("ApprovalEngine"),
  toolIntentService: createServiceToken<ToolIntentService>("ToolIntentService"),
  knowledgeGraphService: createServiceToken<KnowledgeGraphService>(
    "KnowledgeGraphService",
  ),
  modelRouter: createServiceToken<StaticModelRouter>("ModelRouter"),
  cyanLiveService: createServiceToken<CyanLiveService>("CyanLiveService"),
  executionJobRepository: createServiceToken<ExecutionJobRepository>(
    "ExecutionJobRepository",
  ),
  executionJobService: createServiceToken<ExecutionJobService>(
    "ExecutionJobService",
  ),
  serviceRegistry: createServiceToken<ServiceRegistry>("ServiceRegistry"),
  cyanTakeoverBridge: createServiceToken<CyanLiveTakeoverBridge>(
    "CyanLiveTakeoverBridge",
  ),
};

export function createRuntimeContainer(input: {
  observability: ObservabilityClient;
  environment: "development" | "staging" | "production";
  supabase?: SupabaseClient<Database>;
  liveTakeover?: Omit<LiveTakeoverRuntimeDependencies, "supabase">;
}): RuntimeContainer {
  const container = new RuntimeContainer();

  if (input.supabase) {
    container.register({
      token: TOKENS.supabase,
      lifetime: "singleton",
      factory: () => input.supabase,
    });
  }
  container.register({
    token: TOKENS.observability,
    lifetime: "singleton",
    factory: () => input.observability,
  });
  container.register({
    token: TOKENS.eventBus,
    lifetime: "singleton",
    factory: () => new EventBus(),
  });
  container.register({
    token: TOKENS.commandBus,
    lifetime: "singleton",
    dependencies: [TOKENS.eventBus, TOKENS.observability],
    factory: (c) =>
      new CommandBus(
        c.resolve(TOKENS.eventBus),
        c.resolve(TOKENS.observability),
      ),
  });
  container.register({
    token: TOKENS.memoryEngine,
    lifetime: "singleton",
    factory: () => new MemoryEngine(),
  });
  container.register({
    token: TOKENS.knowledgeEngine,
    lifetime: "singleton",
    factory: () => new KnowledgeEngine(),
  });
  container.register({
    token: TOKENS.permissionEvaluator,
    lifetime: "singleton",
    factory: () => new EnterprisePermissionEvaluator(),
  });
  container.register({
    token: TOKENS.toolRegistry,
    lifetime: "singleton",
    factory: () => createDefaultToolRegistry(input.environment),
  });
  container.register({
    token: TOKENS.taskEngine,
    lifetime: "singleton",
    dependencies: [TOKENS.eventBus],
    factory: (c) => new TaskEngine(c.resolve(TOKENS.eventBus)),
  });
  container.register({
    token: TOKENS.agentRuntime,
    lifetime: "singleton",
    dependencies: [TOKENS.eventBus, TOKENS.observability],
    factory: (c) =>
      new AgentRuntime(
        c.resolve(TOKENS.eventBus),
        c.resolve(TOKENS.observability),
      ),
  });
  container.register({
    token: TOKENS.cyanRepository,
    lifetime: "singleton",
    factory: () =>
      input.supabase
        ? new SupabaseCyanIntelligenceRepository(input.supabase)
        : new InMemoryCyanIntelligenceRepository(),
  });

  container.register({
    token: TOKENS.persistentMemoryRepository,
    lifetime: "singleton",
    factory: () =>
      input.supabase
        ? new SupabasePersistentMemoryRepository(input.supabase)
        : undefined,
  });

  container.register({
    token: TOKENS.groundedMemoryRetrievalService,
    lifetime: "singleton",
    dependencies: [TOKENS.persistentMemoryRepository],
    factory: (c) => {
      const repository = c.resolve(TOKENS.persistentMemoryRepository);

      return repository
        ? new GroundedMemoryRetrievalService(repository)
        : undefined;
    },
  });

  container.register({
    token: TOKENS.goalManager,
    lifetime: "singleton",
    dependencies: [TOKENS.cyanRepository],
    factory: (c) => new GoalManager(c.resolve(TOKENS.cyanRepository)),
  });
  container.register({
    token: TOKENS.taskPlanner,
    lifetime: "singleton",
    factory: () => new TaskPlanner(),
  });
  container.register({
    token: TOKENS.planningEngine,
    lifetime: "singleton",
    dependencies: [TOKENS.cyanRepository, TOKENS.taskPlanner],
    factory: (c) =>
      new PlanningEngine(
        c.resolve(TOKENS.cyanRepository),
        c.resolve(TOKENS.taskPlanner),
      ),
  });
  container.register({
    token: TOKENS.decisionEngine,
    lifetime: "singleton",
    dependencies: [TOKENS.cyanRepository],
    factory: (c) => new DecisionEngine(c.resolve(TOKENS.cyanRepository)),
  });
  container.register({
    token: TOKENS.reflectionEngine,
    lifetime: "singleton",
    dependencies: [TOKENS.cyanRepository],
    factory: (c) => new ReflectionEngine(c.resolve(TOKENS.cyanRepository)),
  });
  container.register({
    token: TOKENS.conversationManager,
    lifetime: "singleton",
    dependencies: [TOKENS.cyanRepository],
    factory: (c) => new ConversationManager(c.resolve(TOKENS.cyanRepository)),
  });
  container.register({
    token: TOKENS.reasoningPipeline,
    lifetime: "singleton",
    factory: () => new ReasoningPipeline(),
  });
  container.register({
    token: TOKENS.executionCoordinator,
    lifetime: "singleton",
    dependencies: [
      TOKENS.cyanRepository,
      TOKENS.permissionEvaluator,
      TOKENS.taskEngine,
      TOKENS.eventBus,
      TOKENS.observability,
    ],
    factory: (c) =>
      new ExecutionCoordinator(
        c.resolve(TOKENS.cyanRepository),
        c.resolve(TOKENS.permissionEvaluator),
        c.resolve(TOKENS.taskEngine),
        c.resolve(TOKENS.eventBus),
        c.resolve(TOKENS.observability),
      ),
  });
  container.register({
    token: TOKENS.cyanCore,
    lifetime: "singleton",
    dependencies: [
      TOKENS.memoryEngine,
      TOKENS.taskEngine,
      TOKENS.agentRuntime,
      TOKENS.observability,
      TOKENS.goalManager,
      TOKENS.planningEngine,
      TOKENS.decisionEngine,
      TOKENS.conversationManager,
      TOKENS.reasoningPipeline,
      TOKENS.executionCoordinator,
    ],
    factory: (c) =>
      new CyanCore(
        c.resolve(TOKENS.memoryEngine),
        c.resolve(TOKENS.taskEngine),
        c.resolve(TOKENS.agentRuntime),
        c.resolve(TOKENS.observability),
        {
          goals: c.resolve(TOKENS.goalManager),
          planning: c.resolve(TOKENS.planningEngine),
          decisions: c.resolve(TOKENS.decisionEngine),
          conversations: c.resolve(TOKENS.conversationManager),
          reasoning: c.resolve(TOKENS.reasoningPipeline),
          execution: c.resolve(TOKENS.executionCoordinator),
        },
      ),
  });

  container.register({
    token: TOKENS.requestInterpreter,
    lifetime: "singleton",
    factory: () => new RequestInterpreter(),
  });
  container.register({
    token: TOKENS.contextAssemblyService,
    lifetime: "singleton",
    dependencies: [TOKENS.cyanRepository, TOKENS.toolRegistry],
    factory: (c) =>
      new ContextAssemblyService(
        c.resolve(TOKENS.cyanRepository),
        c.resolve(TOKENS.toolRegistry),
      ),
  });
  container.register({
    token: TOKENS.approvalEngine,
    lifetime: "singleton",
    dependencies: [TOKENS.cyanRepository],
    factory: (c) => new ApprovalEngine(c.resolve(TOKENS.cyanRepository)),
  });
  container.register({
    token: TOKENS.toolIntentService,
    lifetime: "singleton",
    dependencies: [TOKENS.cyanRepository, TOKENS.toolRegistry],
    factory: (c) =>
      new ToolIntentService(
        c.resolve(TOKENS.cyanRepository),
        c.resolve(TOKENS.toolRegistry),
        input.environment,
      ),
  });
  container.register({
    token: TOKENS.knowledgeGraphService,
    lifetime: "singleton",
    factory: () => new KnowledgeGraphService(),
  });
  container.register({
    token: TOKENS.modelRouter,
    lifetime: "singleton",
    factory: () =>
      new StaticModelRouter([new DeterministicModelProvider().config]),
  });
  container.register({
    token: TOKENS.cyanLiveService,
    lifetime: "singleton",
    dependencies: [
      TOKENS.cyanRepository,
      TOKENS.conversationManager,
      TOKENS.goalManager,
      TOKENS.planningEngine,
      TOKENS.decisionEngine,
      TOKENS.reasoningPipeline,
      TOKENS.requestInterpreter,
      TOKENS.contextAssemblyService,
      TOKENS.approvalEngine,
      TOKENS.toolIntentService,
      TOKENS.knowledgeGraphService,
      TOKENS.observability,
      TOKENS.persistentMemoryRepository,
      TOKENS.groundedMemoryRetrievalService,
    ],
    factory: (c) =>
      new CyanLiveService(
        c.resolve(TOKENS.cyanRepository),
        c.resolve(TOKENS.conversationManager),
        c.resolve(TOKENS.goalManager),
        c.resolve(TOKENS.planningEngine),
        c.resolve(TOKENS.decisionEngine),
        c.resolve(TOKENS.reasoningPipeline),
        c.resolve(TOKENS.requestInterpreter),
        c.resolve(TOKENS.contextAssemblyService),
        c.resolve(TOKENS.approvalEngine),
        c.resolve(TOKENS.toolIntentService),
        c.resolve(TOKENS.knowledgeGraphService),
        c.resolve(TOKENS.observability),
        input.supabase,
        c.resolve(TOKENS.persistentMemoryRepository),
        c.resolve(TOKENS.groundedMemoryRetrievalService),
      ),
  });

  container.register({
    token: TOKENS.executionJobRepository,
    lifetime: "singleton",
    factory: () => {
      if (!input.supabase) {
        throw new Error("Persistent execution queue requires Supabase.");
      }

      return new SupabaseExecutionJobRepository(input.supabase);
    },
  });

  container.register({
    token: TOKENS.executionJobService,
    lifetime: "singleton",
    dependencies: [TOKENS.executionJobRepository],
    factory: (c) =>
      new ExecutionJobService(c.resolve(TOKENS.executionJobRepository)),
  });

  container.register({
    token: TOKENS.cyanTakeoverBridge,
    lifetime: "singleton",
    factory: () => {
      if (!input.supabase) {
        throw new Error("Cyan live takeover requires authenticated Supabase.");
      }

      if (!input.liveTakeover) {
        throw new Error(
          "Cyan live takeover dependencies were not supplied by the trusted application bootstrap.",
        );
      }

      return createLiveTakeoverRuntime({
        supabase: input.supabase,
        ...input.liveTakeover,
      }).takeoverBridge;
    },
  });

  container.register({
    token: TOKENS.serviceRegistry,
    lifetime: "singleton",
    factory: () => createDefaultServiceRegistry(),
  });
  container.validate();
  return container;
}
